import { ComponentProps, useCallback, useMemo } from 'react';
import { Drawer, message } from 'antd';
import styled from 'styled-components';

import { Close } from '@assets/icons';
import { UnstyledButton } from '@components/UnstyledButton/UnstyledButton';
import { useCastingContact } from '@hooks/shared/useCastingContact';
import { useViewportWidth } from '@hooks/shared/useViewportWidth';
import {
  getApplicationDeadlineInfo,
  handleJobPostDescriptionDisplay,
  JobPostInput,
} from '@lib/utils/applicationHelper';
import theme from '@styles/theme';
import { StyledGapContainer } from '@views/auth/signup/styles';

import { CastingContactInfo } from '../components/CastingContactInfo';
import { JobPostApplySection } from '../components/JobPostApplySection';
import { JobPostDescription } from '../components/JobPostDescription';
import { PostDatePosted } from '../components/PostDatePosted';
import { PostHeader } from '../components/PostHeader';
import { PostMainInfo } from '../components/PostMainInfo';
import { PostSecondaryInfo } from '../components/PostSecondaryInfo';
import { ShareApplicationButton } from '../components/ShareButton';

type JobsBoardPostDrawerProps = {
  jobPost: JobsBoardApplication;
  closeDrawer: () => void;
} & ComponentProps<typeof Drawer>;
export const JobsBoardPostDrawer = ({
  jobPost,
  closeDrawer,
  ...props
}: JobsBoardPostDrawerProps) => {
  return (
    <StyledDrawer {...props} jobPost={jobPost} closeDrawer={closeDrawer} />
  );
};

const DrawerContent = (props: JobsBoardPostDrawerProps) => {
  const { jobPost, closeDrawer } = props;

  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth <= 768;

  const { hasDeadline } = useMemo(
    () =>
      getApplicationDeadlineInfo(
        jobPost?.jobs_board_close_submissions_date_time,
        jobPost?.application_status,
        jobPost?.project_location?.country
      ),
    [
      jobPost?.jobs_board_close_submissions_date_time,
      jobPost?.application_status,
      jobPost?.project_location?.country,
    ]
  );

  const castingContact = useCastingContact({
    projectLocationCountry: jobPost?.project_location?.country,
    projectCastingContactEmail: jobPost?.project_casting_contact_email,
    projectCastingContactPhone: jobPost?.project_casting_contact_phone,
    projectCompanyName: jobPost?.project_casting_company_name,
  });

  const onShareButtonClick = useCallback(() => {
    if (!jobPost?.application_link) {
      message.error('Something went wrong');
      return;
    }

    const url = `${window.location.origin}/apps/${jobPost?.application_link}`;
    navigator.clipboard.writeText(url);
    message.success('Link copied');
  }, [jobPost?.application_link]);

  const {
    hasRateNotes,
    hasDescription,
    unionFittingRateNote,
    unionInterviewRateNote,
    nonUnionFittingRateNote,
    nonUnionInterviewRateNote,
  } = useMemo(
    () => handleJobPostDescriptionDisplay(jobPost as JobPostInput),
    [jobPost]
  );

  return (
    <Drawer
      {...props}
      rootClassName={props?.className}
      className={undefined}
      destroyOnClose
      closeIcon={null}
      placement={isMobile ? 'bottom' : 'right'}
    >
      <StyledHeader>
        <div className='job-details-title'>
          <UnstyledButton onClick={closeDrawer} className='close-button'>
            <Close />
          </UnstyledButton>
          <div className='job-details-title-text'>Job Details</div>
        </div>
        <ShareApplicationButton onShareButtonClick={onShareButtonClick} />
      </StyledHeader>
      <StyledBody hasDeadline={hasDeadline}>
        <StyledGapContainer
          gap={24}
          style={{
            borderBottom: `1px solid ${theme.palette.stroke[0]}`,
            paddingBottom: '24px',
          }}
        >
          <PostHeader
            postRoleName={jobPost?.role?.name}
            postProjectName={jobPost?.project_name}
            postProjectLocation={jobPost?.project_location}
            postStatus={jobPost?.application_status}
            postCloseDate={jobPost?.jobs_board_close_submissions_date_time}
          />
          <StyledGapContainer gap={12}>
            <PostMainInfo post={jobPost} />
            <div className='line-divider' />
            <PostSecondaryInfo post={jobPost} />
          </StyledGapContainer>
          {(hasRateNotes || hasDescription) && (
            <JobPostDescription
              hasDescription={hasDescription}
              description={jobPost?.description}
              unionFittingRateNote={unionFittingRateNote}
              unionInterviewRateNote={unionInterviewRateNote}
              nonUnionFittingRateNote={nonUnionFittingRateNote}
              nonUnionInterviewRateNote={nonUnionInterviewRateNote}
              hasRateNotes={hasRateNotes}
            />
          )}
          <PostDatePosted
            jobs_board_date_time_posted={jobPost?.jobs_board_date_time_posted}
            projectCountry={jobPost?.project_location?.country}
          />
        </StyledGapContainer>
        <CastingContactInfo
          castingContact={castingContact?.castingContact}
          castingPhone={castingContact?.castingPhone}
          castingEmail={castingContact?.castingEmail}
        />
      </StyledBody>
      <StyledFooter jobPost={jobPost} />
    </Drawer>
  );
};

const StyledDrawer = styled(DrawerContent)`
  &.ant-drawer {
    .ant-drawer-mask {
      background-color: transparent;
      @media (max-width: 768px) {
        background-color: rgba(0, 0, 0, 0.45);
      }
    }
    .ant-drawer-content-wrapper {
      top: 65px;
      width: 460px !important;

      .ant-drawer-content {
        .ant-drawer-wrapper-body {
          .ant-drawer-header {
            display: none;
          }

          .ant-drawer-body {
            padding: 0;
          }
        }
      }

      @media (max-width: 768px) {
        width: 100dvw !important;
        top: 80px;
        height: calc(100dvh - 80px) !important;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;

        .ant-drawer-content {
          border-top-left-radius: 16px;
          border-top-right-radius: 16px;
        }
      }
    }
  }
`;

const StyledHeader = styled.div`
  padding: 20px 24px 19px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.palette.stroke[0]};

  @media (max-width: 768px) {
    flex-direction: row-reverse;
    border-bottom: none;
    padding: 12px 16px;
  }

  .job-details-title {
    display: flex;
    align-items: center;
    gap: 24px;

    .close-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;

      @media (max-width: 768px) {
        width: 40px;
        height: 40px;
      }

      svg {
        width: 24px;
        height: 24px;
        path {
          stroke: ${({ theme }) => theme.palette.text[0]};
        }
      }
    }

    .job-details-title-text {
      font-size: 16px;
      line-height: 24px;
      font-weight: 600;

      @media (max-width: 768px) {
        display: none;
      }
    }
  }
`;

type StyledBodyProps = {
  hasDeadline: boolean;
};
const StyledBody = styled.div<StyledBodyProps>`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.stroke[0]};
  height: ${({ hasDeadline }) =>
    hasDeadline ? 'calc(100% - 183px)' : 'calc(100% - 151px)'};
  overflow-y: auto;

  @media (max-width: 768px) {
    height: ${({ hasDeadline }) =>
      hasDeadline ? 'calc(100% - 183px)' : 'calc(100% - 151px)'};
    padding: 12px 20px 24px;
  }

  .line-divider {
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => theme.palette.stroke[0]};
  }
`;

const StyledFooter = styled(JobPostApplySection)`
  padding: 19px 20px 20px;
`;
